import { point } from '@turf/helpers'
import Distance from '@turf/distance'

const genericCoachee = {
  employeeCode: null,
  name: 'Vendedor Genérico',
  jobTitle: 'Vendedor',
}

export default ({ models }) => ({
  hasInvalidCoordinates: (...args) => args.flat().some(item => !item),

  calculateDistance(user, client) {
    const userPoint = point(user)
    const clientPoint = point(client)
    return Distance(userPoint, clientPoint, { units: 'meters' })
  },

  calculateGoals(payload) {
    // TODO:
    // [x] Calcular Empresa
    // [x] Calcular Gerente
    // [x] Calcular Coordenador
    // [ ] Calcular Vendedor Genérico
    const { coach, routes, generic, parameters, teleSales } = payload
    const { outRouteMultiplier } = parameters
    const notAllowanceClients = routes.in
      .filter(
        route =>
          !route.activeAllowance || route.activeAllowance.reason === 'Quinzenal'
      )
      .reduce((acc, cur) => acc + (cur.goalMultiplier ?? 1), 0)
    const clients = routes.in.length
    if (generic || teleSales)
      return {
        minInRoute: 1,
        maxOutRoute: Math.ceil(clients * outRouteMultiplier),
      }
    switch (coach.jobTitle) {
      case 'Empresa':
      case 'Gerente':
        return {
          minInRoute: Math.round(
            notAllowanceClients <= 10 ? notAllowanceClients : 10
          ),
          maxOutRoute: Math.ceil(clients * outRouteMultiplier),
        }
      case 'Coordenador':
        return {
          minInRoute: Math.round(notAllowanceClients * 0.5) || 1,
          maxOutRoute: Math.ceil(clients * outRouteMultiplier),
        }
      default:
        throw new Error('Unknown job title')
    }
  },
  formFilter: teleSales => form => {
    if (teleSales) return form.targetAudience === 'teleSales'
    return form.targetAudience === 'field'
  },
  start(payload) {
    // TODO:
    // [x] Associar as pessoas envolvidas (Coach, Coachee e Vendedor)
    // [x] Associar a rota do Coaching
    // [x] Calcular os objetivos
    // [ ] Tratar Vendedor Genérico
    const {
      parameters: Parameters,
      coach,
      coachee,
      salesman,
      routes: routesRaw,
      weekday,
      forms,
      activeAllowanceClients,
      ...extra
    } = payload
    const routeMapper = route => {
      const activeAllowance = activeAllowanceClients.find(
        item => item.code === route.code
      )
      return {
        ...route,
        activeAllowance,
        scoreMultiplier: activeAllowance?.reason === 'Quinzenal' ? 0.5 : 1,
        goalMultiplier: activeAllowance?.reason === 'Quinzenal' ? 0.5 : 1,
      }
    }
    const routes = {
      ...routesRaw,
      in: routesRaw.in.map(routeMapper),
      out: routesRaw.out.map(routeMapper),
    }
    const generic = coachee.employeeCode === null
    const parameters = Parameters[generic ? 'Generico' : coach.jobTitle]
    const teleSales = coach.teleSales || coachee.teleSales || false
    const goals = this.calculateGoals({
      ...payload,
      parameters,
      generic,
      routes,
      teleSales,
    })
    return models.Coaching.create({
      ...extra,
      coach,
      salesman,
      weekday,
      routes,
      generic,
      parameters,
      teleSales,
      coachingStage: teleSales ? 'process' : 'pre-process',
      forms: forms.filter(this.formFilter(teleSales)),
      activeAllowanceClients,
      date: new Date(),
      coachee: coachee || genericCoachee,
      statistics: {
        goals,
        pdvs: {
          planned: generic
            ? 1
            : routes.in.filter(pdv => !pdv.activeAllowance).length || 1,
        },
      },
    })
  },
  async checkIn(coaching, payload) {
    const { coachingStage } = coaching
    const { stage } = payload
    switch (stage) {
      case 'pre-process':
        if (coachingStage !== stage)
          throw new Error('This coaching already have a pre-process')
        return this.checkInCommon(coaching, payload)
      case 'post-process':
        if (coachingStage === 'process') {
          await this.nextStage(coaching, { apply: true })
        }
        return this.checkInCommon(coaching, payload)
      case 'process':
        await this.checkInProcess(coaching, payload)
        if (payload.pdvStatus !== 'being')
          await this.checkOut(coaching, payload)
        return
      default:
        throw new Error('This coaching is not active')
    }
  },
  async checkInCommon(coaching, payload) {
    const { formId, analytics, ...extra } = payload
    await coaching.stages.push({
      ...extra,
      checkIn: { ...(analytics || {}) },
    })
    if (formId) return this.setStageForm(coaching, payload)
  },
  async checkInProcess(coaching, payload) {
    //
    const { analytics, clientCode, pdvStatus, ...extra } = payload
    const { routes } = coaching
    if (!clientCode) throw new Error('A client code is required for this stage')
    if (!pdvStatus) throw new Error('A status for this client is required')
    const inRoute = routes.in.find(client => client._id === clientCode)
    const route =
      inRoute || coaching.routes.out.find(client => client._id === clientCode)
    if (!route) throw new Error('Route not found')
    const { statistics } = coaching
    if (!inRoute && statistics.pdvs.outRoute === statistics.goals.maxOutRoute) {
      throw new Error(
        'The maximum amount of out routes for this coaching has been exceded'
      )
    }
    route.status = pdvStatus
    const visits = coaching.stages.filter(
      stage => stage?.pdv?.route.toString() === pdv?.route.toString()
    )
    const lastVisit = visits?.[visits.length - 2]
    if (lastVisit) {
      lastVisit.discarded = true
    }
    if (!coaching.teleSales && analytics.location) {
      const { coordinates } = analytics.location.coordinates
      const client = route.address.location.coordinates
      const hasInvalidCoordinates = this.hasInvalidCoordinates(
        client,
        coordinates
      )
      const distance = hasInvalidCoordinates
        ? Infinity
        : this.calculateDistance(coordinates, client)
      const { pdvMinDistance } = coaching.parameters
      analytics.location.distance = distance
      analytics.location.hasInvalidCoordinates = hasInvalidCoordinates
      const inRange = !pdvMinDistance || distance < pdvMinDistance
      analytics.location.inRange = inRange
      analytics.location.approved = inRange || hasInvalidCoordinates
      analytics.location.approval =
        inRange || hasInvalidCoordinates ? 'auto' : 'waiting'
    }
    const pdv = {
      inRoute: !!inRoute,
      status: pdvStatus,
      route: route._id,
      clientCode: route.code,
      activeAllowance: route.activeAllowance,
      name: route.companyName,
      location:
        coaching.teleSales || !route.address?.location?.coordinates
          ? null
          : {
              coordinates: [
                parseFloat(route.address.location.coordinates[0]),
                parseFloat(route.address.location.coordinates[1]),
              ],
            },
    }
    await this.checkInCommon(coaching, {
      analytics,
      pdv,
      pdvStatus,
      ...extra,
    })
  },
  getCurrentStage(coaching) {
    return coaching.stages.find(stage => !stage.completed)
  },
  async setForm(coaching, payload) {
    const currentStage = await this.getCurrentStage(coaching)
    if (!currentStage) throw new Error('There is no current stage')
    if (currentStage.form) throw new Error('This stage already have a form')
    const { formId } = payload
    const form = coaching.forms.find(item => item.id === formId)
    if (!form) throw new Error('Form not found')
    if (form.stage !== coaching.coachingStage)
      throw new Error('The form does not belong to this stage')
    currentStage.form = JSON.parse(JSON.stringify(form))
  },
  getCurrentQuestion(coaching) {
    const currentStage = this.getCurrentStage(coaching)
    if (!currentStage) throw new Error('There is no current stage')
    if (!currentStage.form) throw new Error('The form is undefined')
    return currentStage.form.questions.find(({ answer }) => !answer)
  },
  async answerQuestion(coaching, payload) {
    const { answer, questionIndex = -1 } = payload
    const currentStage = this.getCurrentStage(coaching)
    if (!currentStage) throw new Error('There is no current stage')
    const question =
      questionIndex < 0
        ? this.getCurrentQuestion(coaching)
        : currentStage.form.questions[questionIndex]
    const answers = typeof answer === 'string' ? [answer] : answer
    question.set('answer', answers)
  },
  async answerDetail(coaching, payload) {
    const { answer, questionIndex } = payload
    const currentStage = this.getCurrentStage(coaching)
    const question = currentStage.form.questions[questionIndex]
    if (question.answer.length < 1) return
    const [option, ...details] = question.answer
    if (details.includes(answer)) {
      question.answer = [option, ...details.filter(item => item !== answer)]
    } else {
      question.answer = [...question.answer, answer]
    }
  },
  async nextStage(coaching, payload = {}) {
    const { apply = true } = payload
    let nextStage
    switch (coaching.coachingStage) {
      case 'pre-process':
        nextStage = 'process'
        break
      case 'process':
        nextStage = 'post-process'
        break
      case 'post-process':
        nextStage = 'feedback'
        break
      case 'feedback':
        nextStage = 'done'
        break
      default:
        throw new Error('This coaching is not active')
    }
    if (apply) {
      coaching.coachingStage = nextStage
    }
    return nextStage
  },
  getMultipliers(clientCode, coaching) {
    const { routes } = coaching
    const finder = item => item.code === clientCode
    return (
      routes.in.find(finder) ||
      routes.out.find(finder) || { scoreMultiplier: 1, goalMultiplier: 1 }
    )
  },
  calculateCheckQuestionScore(question) {
    const alternative = question.alternatives.filter(({ text }) =>
      question.answer.includes(text)
    )
    if (!alternative.length)
      return {
        skip: false,
        score: 0,
        invalidate: false,
        invalidateCoaching: false,
      }
    const invalidatePDV =
      alternative.filter(item => item.invalidatePDV).length > 0
    const invalidateCoaching =
      alternative.filter(item => item.invalidateCoaching).length > 0
    if (invalidatePDV || invalidateCoaching)
      return {
        skip: false,
        score: 0,
        invalidate: invalidatePDV,
        invalidateCoaching: invalidateCoaching,
      }
    const maxWeight = Math.max(...alternative.map(item => item.weight ?? 0))
    return {
      skip: false,
      score: maxWeight ?? 0,
      invalidate: false,
      invalidateCoaching: false,
    }
  },
  calculateRadioQuestionScore(question) {
    const alternative = question.alternatives.find(
      ({ text }) => question.answer[0] === text
    )
    if (!alternative)
      return {
        skip: false,
        score: 0,
        invalidate: false,
        invalidateCoaching: false,
      }
    if (alternative.invalidatePDV || alternative.invalidateCoaching)
      return {
        skip: false,
        score: 0,
        invalidate: alternative.invalidatePDV,
        invalidateCoaching: alternative.invalidateCoaching,
      }
    return {
      skip: false,
      score: alternative.weight ?? 0,
      invalidate: false,
      invalidateCoaching: false,
    }
  },
  calculateQuestionScore(question) {
    if (
      question.skipEvaluation ||
      ['number', 'text'].includes(question.questionType)
    )
      return {
        skip: true,
        score: 0,
        invalidate: false,
        invalidateCoaching: false,
      }
    const evaluateByQuestionType = {
      check: this.calculateCheckQuestionScore,
      radio: this.calculateRadioQuestionScore,
    }
    return evaluateByQuestionType[question.questionType]
      ? evaluateByQuestionType[question.questionType](question)
      : {
          skip: false,
          score: 0,
          invalidate: false,
          invalidateCoaching: false,
        }
  },
  calculateGenericStagesScore(stages) {
    if (!stages?.length) return 0
    let total = 0
    for (const stage of stages) {
      stage.score = {
        answered: 0,
        total: 0,
        grade: 0,
        disqualifyingAnswerGiven: false,
      }
      for (const question of stage.form.questions) {
        stage.score.answered += 1
        const { skip, score, invalidateCoaching } = this.calculateQuestionScore(
          question
        )
        if (invalidateCoaching) stage.score.disqualifyingAnswerGiven = true
        if (skip) continue
        stage.score.total += score * (question.weight ?? 0)
      }
      stage.score.grade = stage.score.total / 100
      total += stage.score.grade
    }
    if (!stages.length) return 0
    return total / stages.length
  },
  calculateProcessStagesScore(stages) {
    if (!stages?.length) return 0
    let total = 0
    let validPdvs = 0
    for (const stage of stages) {
      stage.score = {
        answered: 0,
        total: 0,
        grade: 0,
        disqualifyingAnswerGiven: false,
      }

      if (!stage.pdv || stage.pdv.status !== 'being') continue

      validPdvs += 1

      for (const question of stage.form.questions) {
        stage.score.answered += 1
        const {
          skip,
          score,
          invalidate,
          invalidateCoaching,
        } = this.calculateQuestionScore(question)
        if (invalidateCoaching) stage.score.disqualifyingAnswerGiven = true
        if (invalidate) {
          stage.pdv.invalidated = true
          continue
        }
        if (skip) continue
        stage.score.total += score * (question.weight ?? 0)
      }

      stage.score.grade = stage.score.total / 100
      if (stage.pdv.invalidated) {
        stage.score.grade = 0
      }

      total += stage.score.grade
    }
    if (validPdvs === 0) return 0
    return total / validPdvs
  },
  calculateScoreV2(coaching) {
    const completedStages = coaching.stages.filter(
      stage => stage.completed && !stage.discarded
    )
    const groupedStages = completedStages.reduce((acc, stage) => {
      if (!acc[stage.stage]) {
        acc[stage.stage] = [stage]
      } else {
        acc[stage.stage].push(stage)
      }
      return acc
    }, {})
    const scoreByStages = coaching.teleSales
      ? {
          process: {
            score: this.calculateProcessStagesScore(groupedStages.process),
            weight: 100, // 100%
          },
          'post-process': {
            score: this.calculateGenericStagesScore(
              groupedStages['post-process']
            ),
            weight: 0, // 0%
          },
        }
      : {
          'pre-process': {
            score: this.calculateGenericStagesScore(
              groupedStages['pre-process']
            ),
            weight: 10, // 10%
          },
          process: {
            score: this.calculateProcessStagesScore(groupedStages.process),
            weight: 70, // 70%
          },
          'post-process': {
            score: this.calculateGenericStagesScore(
              groupedStages['post-process']
            ),
            weight: 20, // 20%
          },
        }
    const total = Object.entries(scoreByStages).reduce((acc, [_, value]) => {
      return acc + value.score * value.weight
    }, 0)
    coaching.score.pdvs = completedStages.filter(
      stage => stage.stage === 'process' && stage.pdv && stage.pdv === 'being'
    ).length
    const shouldDisqualifyCoaching = completedStages.some(
      s => s.score.disqualifyingAnswerGiven
    )
    coaching.score.total = shouldDisqualifyCoaching ? 0 : total
    coaching.score.grade = coaching.score.total / 100
    if (!coaching.appVersion?.includes(window.$versionTag)) {
      if (Array.isArray(coaching.appVersion))
        coaching.appVersion.push(window.$versionTag)
      else {
        coaching.appVersion = [window.$versionTag]
      }
    }
  },
  async checkOut(coaching, payload = {}, applyNextStage = true) {
    const { analytics } = payload
    const currentStage = await this.getCurrentStage(coaching)
    currentStage.checkOut = analytics || {}
    if (coaching.coachingStage === 'process') {
      const { pdv, discarded } = currentStage
      const { scoreMultiplier = 1, goalMultiplier = 1 } = this.getMultipliers(
        pdv.clientCode,
        coaching
      )
      if (!discarded) await this.computeStatistics(coaching)
      currentStage.completed = true
      const { visitedAllowanceClients, statistics } = coaching
      if (
        pdv.activeAllowance &&
        pdv.activeAllowance.reason !== 'Quinzenal' &&
        pdv.inRoute &&
        !visitedAllowanceClients.includes(pdv.clientCode)
      ) {
        visitedAllowanceClients.push(pdv.clientCode)
        statistics.goals.minInRoute += goalMultiplier
        statistics.pdvs.planned += goalMultiplier
        statistics.pdvs.inRoute += goalMultiplier
      }
      if (pdv.status !== 'being') return
      this.calculateScoreV2(coaching)
      return
    }
    currentStage.completed = true
    this.calculateScoreV2(coaching)
    if (applyNextStage) {
      await this.nextStage(coaching)
    }
  },
  async computeStatistics(coaching) {
    const currentStage = await this.getCurrentStage(coaching)
    const { invalidated, pdv } = currentStage
    const { goals, pdvs } = coaching.statistics
    const visits = coaching.stages.filter(
      stage => stage?.pdv?.clientCode === pdv?.clientCode
    )
    const lastVisit = visits?.[visits.length - 2]
    if (pdv.status === 'inactive') {
      if (lastVisit) {
        if (lastVisit.pdv.status === 'inactive') return
        pdvs.visited--
        pdvs[pdv.inRoute ? 'inRoute' : 'outRoute']--
        goals.minRoutesAchivied = pdvs.visited >= goals.minInRoute
      }
      return
    }
    if (
      lastVisit &&
      lastVisit.pdv.status !== 'inactive' &&
      !lastVisit.discarded
    )
      return
    pdvs.visited++
    pdvs[pdv.inRoute ? 'inRoute' : 'outRoute']++
    goals.minRoutesAchivied = pdvs.visited >= goals.minInRoute
    pdvs.invalid += invalidated ? 1 : 0
  },
  async finish(coaching, payload) {
    if (coaching.coachingStage !== 'feedback')
      throw new Error('This coaching is not waiting a feedback now')
    coaching.feedbacks = payload
    coaching.coachingStage = 'done'
  },
  async discardCoaching(coaching) {
    coaching.coachingStage = 'discarded'
  },
  async expireCoaching(coaching) {
    coaching.expired = true
  },
  async abortPreProcess(coaching, payload) {
    const currentStage = await this.getCurrentStage(coaching)
    currentStage.discarded = true
    await this.checkOut(coaching, { ...payload, discarded: true }, false)
  },
  async abortProcess(coaching, payload) {
    const { reason } = payload
    const currentStage = await this.getCurrentStage(coaching)
    currentStage.pdv.status = reason
    currentStage.discarded = reason === 'discard'
    await this.checkOut(coaching, {
      ...payload,
      discarded: reason === 'discard',
    })
  },
  async clientObservation(coaching, payload) {
    const currentStage = await this.getCurrentStage(coaching)
    currentStage.pdv.coachingObservation = payload.observation
  },
  async clientPotential(coaching, payload) {
    const currentStage = await this.getCurrentStage(coaching)
    currentStage.pdv.potential = payload
  },
})
